import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, brandingApi } from '../api';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, KeyRound } from 'lucide-react';
import { CredBloomMark, CredBloomWordmark } from '../components/CredBloomBrand';

export default function Login() {
  const { slug } = useParams(); // present only on /vendor/:slug/login
  const [key, setKey] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null); // null until fetched
  const { login } = useAuth();
  const navigate = useNavigate();

  // White-label: fetch branding for the URL slug (no auth)
  useEffect(() => {
    if (!slug) { setBranding({ app_name: 'CredBloom', primary_color: null, logo_url: null }); return; }
    brandingApi.bySlug(slug)
      .then(({ data }) => setBranding(data))
      .catch(() => setBranding({ app_name: 'CredBloom', primary_color: null, logo_url: null }));
  }, [slug]);

  // Reflect branding into the page title so the browser tab matches
  useEffect(() => {
    if (branding?.app_name) document.title = `${branding.app_name} — Sign in`;
  }, [branding]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return toast.error('Please enter your login key');
    if (!email || !password) return toast.error('Please fill in all fields');
    setLoading(true);
    try {
      const { data } = await authApi.login({ key: key.trim(), email, password });
      login(data.token, data.user);
      const role = data.user.role;
      if (role === 'super_admin') navigate('/super-admin');
      else if (role === 'vendor') navigate('/vendor-admin');
      else if (role === 'business_admin') navigate('/admin');
      else navigate('/dept');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials or key');
    } finally {
      setLoading(false);
    }
  };

  const appName = branding?.app_name || 'CredBloom';
  const brandColor = branding?.primary_color;
  const logoUrl = branding?.logo_url;
  const initial = appName.trim()[0]?.toUpperCase() || 'C';
  const isCredBloom = appName === 'CredBloom' && !brandColor && !logoUrl;
  const tileStyle = brandColor ? { backgroundColor: brandColor } : undefined;
  const submitStyle = brandColor ? { backgroundColor: brandColor } : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8" data-testid="login-brand">
          {logoUrl ? (
            <img src={logoUrl} alt={appName} className="w-14 h-14 rounded-2xl object-cover shadow-lg mb-4" />
          ) : isCredBloom ? (
            <CredBloomMark className="mb-4" />
          ) : (
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg mb-4" style={tileStyle}>
              <span className="text-primary-foreground font-bold text-2xl" style={{ fontFamily: 'Manrope' }}>{initial}</span>
            </div>
          )}
          <div data-testid="login-app-name">
            {isCredBloom ? (
              <CredBloomWordmark size="lg" />
            ) : (
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>{appName}</h1>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Reputation Management Platform</p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl shadow-sm p-8">
          <h2 className="text-xl font-semibold text-foreground mb-1" style={{ fontFamily: 'Manrope' }}>Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-6">Sign in to your account to continue</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Key field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-key">
                Login Key
              </label>
              <div className="relative">
                <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="login-key"
                  type="text"
                  value={key}
                  onChange={e => setKey(e.target.value)}
                  placeholder="Your Business Key (e.g. ABC-123456)"
                  autoComplete="off"
                  data-testid="login-key-input"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all font-mono tracking-wide uppercase placeholder:normal-case placeholder:tracking-normal"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="email">
                Email address
              </label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                data-testid="login-email-input"
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              style={submitStyle}
              className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
